"use client";
import { motion } from "framer-motion";

const SkillBadge = ({ name }) => (
    <span className="px-3 py-1 text-sm rounded-full border border-white/5 bg-white/5 backdrop-blur-md text-muted-foreground hover:bg-white/10 hover:border-[#ff5500]/30 hover:text-white transition-all duration-300 pointer-events-auto">
        {name}
    </span>
);

export default function About() {
    const skills = [
        "JavaScript", "React", "Next.js", "Node.js", "TypeScript",
        "PostgreSQL", "MongoDB", "AWS", "Docker", "Tailwind CSS",
        "Python", "WebAssembly", "MUI", "HTML", "CSS", "Jenkins",
        "Selenium", "Git", "SEO", "Penetration Testing", "Network Security",
        "Ethical Hacking", "JWT"];

    const journey = [
        {
            year: "2025 - Present",
            role: "DevOps IG Lead @ Mulearn Foundation",
            desc: "Organizing workshops, mentoring peers, and helping build the student DevOps community across Kerala."
        },
        {
            year: "2026 - Present",
            role: "Tech Lead @ Mulearn MCE",
            desc: "Teaching students hands-on software development, running coding bootcamps, and helping them transition to engineering roles."
        },
        {
            year: "2026 - Present",
            role: "Founder @ Edurea",
            desc: "Building a platform for entrance exam analytics and rank prediction to help students plan their prep."
        }
    ];

    return (
        <section id="about" className="py-24 relative overflow-hidden bg-black">
            {/* Ambient Background Glows */}
            <div className="absolute top-1/4 left-0 w-[30vw] h-[30vw] bg-[#ff5500]/5 rounded-full blur-[120px] mix-blend-screen pointer-events-none z-0" />
            <div className="absolute bottom-1/4 right-0 w-[25vw] h-[25vw] bg-[#ff5500]/3 rounded-full blur-[100px] mix-blend-screen pointer-events-none z-0" />

            <div className="container px-4 mx-auto max-w-7xl relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                    {/* About Text & Tech Arsenal */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-3xl font-bold text-white mb-8">About Me</h2>
                        <div className="text-muted-foreground space-y-6 text-lg leading-relaxed">
                            <p>
                                I'm <span className="text-white font-semibold">Adhwaith A S</span>, a developer who cares about both clean code and user experience.
                            </p>
                            <p>
                                I write software that works, looks clean, and doesn't crash. I focus on database performance, secure APIs, and responsive frontends—paying close attention to the small details that make using an app feel smooth.
                            </p>
                            <p>
                                Right now, I'm focusing on building fast, accessible web apps and developer tools.
                            </p>
                        </div>

                        <div className="mt-12">
                            <h3 className="text-sm font-mono text-muted-foreground mb-6 uppercase tracking-wider">Tech Arsenal</h3>
                            <div className="flex flex-wrap gap-3">
                                {skills.map(s => <SkillBadge key={s} name={s} />)}
                            </div>
                        </div>
                    </motion.div>

                    {/* Timeline / Journey */}
                    <div>
                        <motion.h2 
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.6 }}
                            className="text-3xl font-bold text-white mb-8"
                        >
                            Journey
                        </motion.h2>
                        <div className="border-l border-white/10 ml-3 space-y-12">
                            {journey.map((item, idx) => (
                                <motion.div 
                                    key={idx} 
                                    initial={{ opacity: 0, x: -25 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: idx * 0.15 }}
                                    className="relative pl-8"
                                >
                                    <div className="absolute left-[-5px] top-2 w-2.5 h-2.5 rounded-full bg-white ring-4 ring-black" />
                                    <span className="text-xs font-mono text-[#ff5500] mb-2 block">{item.year}</span>
                                    <h3 className="text-xl font-semibold text-white mb-2">{item.role}</h3>
                                    <p className="text-muted-foreground">
                                        {item.desc}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                </div>

                <div className="w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent my-20" />

                {/* Resume download section */}
                <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="flex flex-col items-center gap-8 relative z-10"
                >
                    <div className="text-center space-y-4">
                        <h2 className="text-3xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-white to-white/40 font-title tracking-tighter">
                            Career Dossier
                        </h2>
                        <p className="text-muted-foreground font-mono text-xs md:text-sm tracking-[0.3em] uppercase opacity-70">
                            Professional Experience & Qualifications
                        </p>
                    </div>

                    <a
                        href="/resume.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative inline-flex h-12 overflow-hidden rounded-full p-[1px] focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-50 group"
                    >
                        <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#ff9966_0%,#ff5500_50%,#ff9966_100%)]" />
                        <span className="inline-flex h-full w-full cursor-pointer items-center justify-center rounded-full bg-slate-950 px-8 py-1 text-sm font-medium text-white backdrop-blur-3xl transition-all group-hover:bg-slate-950/80 gap-2">
                            Download Resume
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-download group-hover:translate-y-0.5 transition-transform"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>
                        </span>
                    </a>
                </motion.div>
            </div>
        </section>
    );
}
